/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable no-underscore-dangle */
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';
import { ICurrentUserService } from './icurrentuser.service';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { AppConfig } from '../../models/AppConfig';
import { TelemetryService } from '../telemetry.service';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { getOpsViewerScopes } from './auth-scopes';

@Injectable()
export class CurrentUserService implements ICurrentUserService {
  constructor(private _authService: MsalService, private _telemetry: TelemetryService) {
  }
  public getUserEmail(): string | null {
    const account = this.getOpsAccount();
    if (!account) {
      return null;
    }
    return this.getAccountEmail(account);
  }
  public getUser(): AccountInfo | null {
    return this.getOpsAccount();
  }

  private getOpsAccount(): AccountInfo | null {
    const accounts = this._authService.instance.getAllAccounts();
    if (!accounts || accounts.length === 0) { return null; }

    const existingActive = this._authService.instance.getActiveAccount();
    if (existingActive && this.matchesCurrentPolicy(existingActive)) {
      this._telemetry.setAuthenticatedUserContext(this.getAccountEmail(existingActive));
      return existingActive;
    }

    let candidates = accounts;
    const policy = this.getCurrentPolicy();
    if (policy) {
      const policyMatches = accounts.filter(a => this.matchesPolicy(a, policy));
      if (policyMatches.length > 0) { candidates = policyMatches; }
    }

    const multipleCandidates = candidates.length > 1;
    if (multipleCandidates) {
      // Sort newest first based on auth_time or iat
      candidates.sort((a: any, b: any) => {
        const at = (a.idTokenClaims?.auth_time || a.idTokenClaims?.iat || 0);
        const bt = (b.idTokenClaims?.auth_time || b.idTokenClaims?.iat || 0);
        return bt - at;
      });
    }

    const chosen = candidates[0];
    if (chosen) {
      this._authService.instance.setActiveAccount(chosen);
      this._telemetry.setAuthenticatedUserContext(this.getAccountEmail(chosen));
    }

    if (multipleCandidates) {
      this._telemetry.trackTrace({ message: 'Multiple accounts found. Using newest by token time.', severityLevel: SeverityLevel.Warning }, {
        policy: policy || 'none',
        candidates: JSON.stringify(candidates.map(a => this.toAccountTrace(a))),
        chosen: chosen ? JSON.stringify(this.toAccountTrace(chosen)) : 'none'
      });
    }

    if (!chosen) {
      const accountsDebug = accounts ? JSON.stringify(accounts) : 'no accounts';
      this._telemetry.trackTrace({ message: `No matching account found for current policy. ${accountsDebug}`, severityLevel: SeverityLevel.Warning });
    }
    return chosen ?? null;
  }

  private getCurrentPolicy(): string {
    const authority: string = AppConfig.GLOBAL_CONFIG.authority;
    const segments = authority.split('/').filter(s => !!s);
    return segments.length > 0 ? segments[segments.length - 1].toLowerCase() : '';
  }

  private toAccountTrace(account: AccountInfo): object {
    const claims: any = account.idTokenClaims || {};
    return {
      email: this.getAccountEmail(account) || '',
      username: account.username || '',
      localAccountId: account.localAccountId || '',
      homeAccountId: account.homeAccountId || '',
      tenantId: account.tenantId || '',
      policy: claims['tfp'] || claims['acr'] || '',
      auth_time: claims['auth_time'] || '',
      iat: claims['iat'] || ''
    };
  }

  private matchesCurrentPolicy(account: AccountInfo): boolean {
    const policy = this.getCurrentPolicy();
    return !policy || this.matchesPolicy(account, policy);
  }

  private matchesPolicy(account: AccountInfo, policy: string): boolean {
    const claims: any = account.idTokenClaims || {};
    const policyClaim = claims['tfp'] || claims['acr'];
    return (policyClaim || '').toLowerCase() === policy;
  }

  private getAccountEmail(account: AccountInfo): string | null {
    const claims: any = account.idTokenClaims || {};
    return (claims.emails && claims.emails[0])
      || claims.email
      || claims.preferred_username
      || claims.upn
      || claims.login_hint
      || claims['signInNames.emailAddress']
      || claims['signInNames.email']
      || claims.signInNames?.emailAddress
      || account.username
      || null;
  }

  public getAccessToken(): Observable<string> {
    const account = this.getOpsAccount();
    if (!account) {
      return throwError(() => new Error('No active account available for token acquisition.'));
    }
    return this._authService.acquireTokenSilent({ scopes: getOpsViewerScopes(), account }).pipe(first(), map(t => t.accessToken), catchError(err => this.handleError(err)));
  }
  private handleError(error: any): Observable<string> {
    if (error instanceof InteractionRequiredAuthError) {
      this._telemetry.trackTrace({ message: 'InteractionRequiredAuthError: redirecting for interaction', severityLevel: SeverityLevel.Warning });
      // Kick off interactive redirect; current observable will error out.
      this._authService.acquireTokenRedirect({ scopes: getOpsViewerScopes() });
    }
    return throwError(() => error);
  }

}
